import React, { useState, useEffect } from 'react';
import { Calculator, DollarSign, Users, Percent } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const LoyaltyProgramROICalculator = () => {
  const [programCosts, setProgramCosts] = useState(50000);
  const [incrementalRevenue, setIncrementalRevenue] = useState(100000);
  const [totalMembers, setTotalMembers] = useState(10000);
  const [activeMembers, setActiveMembers] = useState(7000);
  const [roi, setRoi] = useState(0);
  const [activationRate, setActivationRate] = useState(0);

  useEffect(() => {
    calculateROI();
    calculateActivationRate();
  }, [programCosts, incrementalRevenue, totalMembers, activeMembers]);

  const calculateROI = () => {
    const calculatedRoi = ((incrementalRevenue - programCosts) / programCosts) * 100;
    setRoi(parseFloat(calculatedRoi.toFixed(2)));
  };

  const calculateActivationRate = () => {
    const rate = (activeMembers / totalMembers) * 100;
    setActivationRate(parseFloat(rate.toFixed(2)));
  };

  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/loyalty-program-roi-calculator`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Loyalty Program ROI Calculator: Measure Program Success | Free Tool</title>
        <meta name="description" content="Calculate your Loyalty Program ROI instantly. Measure program effectiveness, track member activation rates, and optimize your loyalty strategy with expert insights." />
        <meta name="keywords" content="loyalty program ROI calculator, customer loyalty metrics, member activation rate, loyalty program performance, customer retention tools" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free Loyalty Program ROI Calculator: Optimize Your Program" />
        <meta property="og:description" content="Calculate your Loyalty Program ROI instantly. Get actionable insights to improve program performance and boost customer retention." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free Loyalty Program ROI Calculator: Optimize Your Program" />
        <meta property="twitter:description" content="Calculate your Loyalty Program ROI instantly. Get actionable insights to improve program performance and boost customer retention." />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Loyalty Program ROI Calculator",
            "description": "Free calculator to measure and optimize loyalty program performance",
            "url": pageUrl,
            "image": logoUrl,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Calculate Loyalty Program ROI",
              "Measure Member Activation Rate",
              "Track Program Performance",
              "Compare with Industry Benchmarks",
              "Get Optimization Strategies"
            ],
            "about": {
              "@type": "Thing",
              "name": "Loyalty Program ROI",
              "description": "The return on investment from customer loyalty programs, measuring program costs against incremental revenue and member engagement."
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Marketing Managers, Customer Success Teams, Business Owners"
            },
            "mainEntity": {
              "@type": "HowTo",
              "name": "How to Calculate Loyalty Program ROI",
              "step": [
                {
                  "@type": "HowToStep",
                  "text": "Enter program costs"
                },
                {
                  "@type": "HowToStep",
                  "text": "Input incremental revenue"
                },
                {
                  "@type": "HowToStep",
                  "text": "Add total and active member counts"
                },
                {
                  "@type": "HowToStep",
                  "text": "View ROI and activation rate metrics"
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        Loyalty Program ROI Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Program Costs</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={programCosts}
                  onChange={(e) => setProgramCosts(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="50000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Incremental Revenue</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={incrementalRevenue}
                  onChange={(e) => setIncrementalRevenue(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="100000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Members</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Users className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={totalMembers}
                  onChange={(e) => setTotalMembers(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="10000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Active Members</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Users className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={activeMembers}
                  onChange={(e) => setActiveMembers(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="7000"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-500">ROI</p>
              <p className="text-4xl font-bold text-indigo-600 flex items-center">
                {roi}%
                <Percent className="w-8 h-8 ml-2 text-indigo-400" />
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Activation Rate</p>
              <p className="text-4xl font-bold text-green-600 flex items-center">
                {activationRate}%
                <Users className="w-8 h-8 ml-2 text-green-400" />
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation & Industry Benchmarks</h2>
        <p className="text-gray-600">
          Your Loyalty Program ROI is {roi}%, which means for every dollar invested in the program, you're generating ${(roi / 100 + 1).toFixed(2)} in incremental revenue. The Activation Rate of {activationRate}% indicates that {activationRate}% of your total members are actively participating in the program.
        </p>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Industry Benchmarks</h3>
        <ul className="list-disc list-inside text-gray-600 mb-4">
          <li>Average Program ROI: 200-400%</li>
          <li>Typical Activation Rate: 65-75%</li>
          <li>Retail Program ROI: 300-500%</li>
          <li>Restaurant Program ROI: 250-350%</li>
          <li>Hotel Program ROI: 400-600%</li>
          <li>Airlines Program ROI: 500-700%</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Program Optimization Strategies</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Personalize rewards based on customer preferences</li>
          <li>Simplify program structure and communication</li>
          <li>Offer a mix of transactional and experiential rewards</li>
          <li>Use gamification elements to increase engagement</li>
          <li>Regularly communicate program benefits and status</li>
          <li>Integrate with overall marketing strategy</li>
          <li>Gather and act on member feedback</li>
          <li>Implement tiered rewards structure</li>
          <li>Create exclusive member events and benefits</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Program Impact Statistics</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Members spend 20-40% more than non-members</li>
          <li>Active members visit 90% more frequently</li>
          <li>82% of members refer at least one new customer</li>
          <li>Loyalty programs increase customer lifetime value by 30%</li>
          <li>Members are 5x more likely to choose your brand over competitors</li>
        </ul>
      </div>
    </div>
  );
};

export default LoyaltyProgramROICalculator;
